import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { cn } from '../../../lib';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export type SelectCardOption = {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
};

type SelectCardsProps = {
  options: SelectCardOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  name?: string;
  /**
   * Layout mode:
   * - `cards` (default): equal-width grid columns, cards fill container
   * - `small-cards`: cards sized to widest content, uniform width, flex-wrap
   * - `badge`: horizontal pills with icon + label, content-width, flex-wrap
   */
  display?: 'cards' | 'small-cards' | 'badge';
  /** Force a specific number of cards per row. Only applies in 'cards' mode. */
  perRow?: number;
  /** Maximum cards per row. Defaults to 4. Only applies in 'cards' mode. */
  maxPerRow?: number;
};

// Literal class names: Tailwind only keeps what it can read in the source.
// Below md the cards reflow rather than shrink, so long labels stay readable on a phone.
const GRID_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 md:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5',
  6: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-6',
};

function computeGridCols(count: number, maxPerRow: number): number {
  for (let cols = maxPerRow; cols >= 2; cols--) {
    if (count % cols === 0) return cols;
  }
  return Math.min(count, maxPerRow);
}

export const SelectCards = ({
  options,
  value,
  onChange,
  disabled,
  name,
  display = 'cards',
  perRow,
  maxPerRow = 4,
}: SelectCardsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [uniformWidth, setUniformWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (display !== 'small-cards' || !containerRef.current) return;
    const buttons = containerRef.current.querySelectorAll<HTMLButtonElement>('button');
    const maxWidth = Math.max(...Array.from(buttons).map(b => b.getBoundingClientRect().width));
    if (maxWidth > 0) setUniformWidth(Math.ceil(maxWidth));
  }, [display, options]);

  if (display === 'badge') {
    return (
      <Group role="radiogroup" aria-label={name} align="stretch" wrap gap={2}>
        {options.map(option => {
          const isSelected = value === option.value;
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => !disabled && onChange(option.value)}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border-2 px-3 py-1.5 text-sm font-medium transition-all duration-200',
                isSelected
                  ? 'border-primary bg-primary/5 text-primary shadow-sm'
                  : 'border-input bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {Icon && (
                <Icon
                  className={cn(
                    'h-3.5 w-3.5 shrink-0',
                    isSelected ? 'text-primary' : 'text-muted-foreground',
                  )}
                />
              )}
              {option.label}
            </button>
          );
        })}
      </Group>
    );
  }

  if (display === 'small-cards') {
    return (
      <Group ref={containerRef} role="radiogroup" aria-label={name} align="stretch" wrap gap={2}>
        {options.map(option => {
          const isSelected = value === option.value;
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => !disabled && onChange(option.value)}
              style={
                uniformWidth !== undefined
                  ? { width: `${uniformWidth}px`, flexShrink: 0 }
                  : { flexShrink: 0 }
              }
              className={cn(
                'group flex cursor-pointer flex-col items-center gap-2.5 rounded-xl border-2 px-3 py-4 text-sm font-medium transition-all duration-200',
                isSelected
                  ? 'border-primary bg-primary/5 text-primary shadow-sm'
                  : 'border-input bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full transition-all duration-200',
                  isSelected
                    ? 'bg-primary text-primary-foreground shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]'
                    : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary',
                )}
              >
                {Icon ? <Icon className="h-5 w-5" /> : null}
              </div>
              <Text inline size="xs" className="text-center leading-tight">
                {option.label}
              </Text>
            </button>
          );
        })}
      </Group>
    );
  }

  // 'cards' mode (default): equal-width grid columns
  const cols = perRow ?? computeGridCols(options.length, maxPerRow);

  return (
    <div
      role="radiogroup"
      aria-label={name}
      className={cn(
        'grid gap-2',
        GRID_COLUMNS[cols] ?? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
      )}
    >
      {options.map(option => {
        const isSelected = value === option.value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => !disabled && onChange(option.value)}
            className={cn(
              'group flex cursor-pointer flex-col items-center gap-2.5 rounded-xl border-2 px-3 py-4 text-sm font-medium transition-all duration-200',
              isSelected
                ? 'border-primary bg-primary/5 text-primary shadow-sm'
                : 'border-input bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-full transition-all duration-200',
                isSelected
                  ? 'bg-primary text-primary-foreground shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]'
                  : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary',
              )}
            >
              {Icon ? <Icon className="h-5 w-5" /> : null}
            </div>
            <Text inline size="xs" className="text-center leading-tight">
              {option.label}
            </Text>
          </button>
        );
      })}
    </div>
  );
};
